import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PDFReader = () => {
  const location = useLocation();
  const { link, title } = location.state || {};
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Variables pour la gestion du pinch zoom
  const lastTouchDistance = useRef(0);
  const lastScale = useRef(1.0);

  // Détection simple des appareils mobiles
  useEffect(() => {
    const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    setIsMobile(mobileCheck);
    
    // Ne pas définir l'échelle ici, elle sera déterminée lors du rendu
  }, []);

  // Chargement de PDF.js
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.12.313/build/pdf.min.js';
    script.async = true;
    
    script.onload = () => {
      const pdfjsLib = window['pdfjs-dist/build/pdf'];
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.12.313/build/pdf.worker.min.js';
      
      if (link) {
        const loadingTask = pdfjsLib.getDocument(link);
        loadingTask.promise.then(doc => {
          setPdfDoc(doc);
          setTotalPages(doc.numPages);
          setLoading(false);
        }).catch(error => {
          console.error('Erreur lors du chargement du PDF:', error);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    };

    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [link]);

  // Rendu de la page PDF
  useEffect(() => {
    const renderPage = async () => {
      if (!pdfDoc) return;
      
      try {
        const page = await pdfDoc.getPage(currentPage);
        const canvas = canvasRef.current;
        const container = containerRef.current;
        
        if (!canvas || !container) return;
        
        // Obtenir la largeur du conteneur
        const containerWidth = container.clientWidth;
        
        // Obtenir les dimensions de base du PDF
        const originalViewport = page.getViewport({ scale: 1.0 });
        
        // Si l'échelle n'est pas encore définie (null) ou est toujours à la valeur par défaut (1.0),
        // calculer l'échelle pour s'adapter à la largeur du conteneur
        let currentScale = scale;
        if (currentScale === null) {
          // Calculer l'échelle pour que le PDF prenne la largeur du conteneur
          const fitScale = containerWidth / originalViewport.width;
          currentScale = fitScale;
          // Mettre à jour l'état de l'échelle
          setScale(fitScale);
          // Mettre à jour la référence d'échelle pour le pinch zoom
          lastScale.current = fitScale;
        }
        
        // Créer le viewport avec l'échelle finale
        const viewport = page.getViewport({ scale: currentScale });
        
        // Configurer le canvas aux bonnes dimensions
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        // Rendre la page
        const renderContext = {
          canvasContext: canvas.getContext('2d'),
          viewport: viewport
        };
        
        await page.render(renderContext).promise;
        
      } catch (error) {
        console.error('Erreur lors du rendu de la page:', error);
      }
    };
    
    if (pdfDoc) {
      renderPage();
    }
  }, [pdfDoc, currentPage, scale]);

  // Navigation basique
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Zoom basique
  const zoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
  };
  
  // Gestion du pinch zoom
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Calcul de la distance initiale entre les deux doigts
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      lastTouchDistance.current = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
    }
  };
  
  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault(); // Empêcher le scroll/zoom par défaut du navigateur
      
      // Calcul de la nouvelle distance entre les deux doigts
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
      
      // Si nous avons une distance initiale, calculer le facteur de zoom
      if (lastTouchDistance.current > 0) {
        const scaleFactor = currentDistance / lastTouchDistance.current;
        
        // Appliquer le zoom relatif à l'échelle précédente
        const newScale = Math.max(0.5, Math.min(3.0, lastScale.current * scaleFactor));
        setScale(newScale);
      }
    }
  };
  
  const handleTouchEnd = () => {
    // Réinitialiser la distance pour la prochaine opération de pinch
    lastTouchDistance.current = 0;
    // Mettre à jour l'échelle de référence
    lastScale.current = scale;
  };

  // Téléchargement du PDF
  const downloadPDF = () => {
    if (link) {
      const downloadLink = document.createElement('a');
      downloadLink.href = link;
      
      const fileName = title ? `${title}.pdf` : link.split('/').pop();
      downloadLink.setAttribute('download', fileName);
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };
  
  // Réinitialiser le zoom à la taille adaptée à l'écran
  const resetZoom = () => {
    if (!pdfDoc || !containerRef.current) return;
    
    // Récupérer la première page pour calculer le ratio
    pdfDoc.getPage(currentPage).then(page => {
      const containerWidth = containerRef.current.clientWidth;
      const viewport = page.getViewport({ scale: 1.0 });
      const fitScale = containerWidth / viewport.width;
      setScale(fitScale);
    });
  };

  return (
    <>
      <Header />
      <main className="pdfreader-component">
        <div className="fixed-header">
          <h1>{title}</h1>

          <div className="pdf-tools">
            <div className="tool-group">
              <Link to="/lessons" className="tool-button close-btn">
                <svg width="16" height="16" viewBox="0 0 99 99" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M96.8072 2.2679C96.1125 1.57178 95.2873 1.0195 94.3789 0.642687C93.4705 0.26587 92.4967 0.0719093 91.5133 0.0719093C90.5298 0.0719093 89.556 0.26587 88.6476 0.642687C87.7392 1.0195 86.9141 1.57178 86.2194 2.2679L49.5 38.9122L12.7806 2.1928C12.0854 1.4976 11.2601 0.946132 10.3517 0.56989C9.44342 0.193647 8.46988 7.32517e-09 7.48671 0C6.50354 -7.32518e-09 5.53 0.193647 4.62167 0.56989C3.71334 0.946132 2.88801 1.4976 2.1928 2.1928C1.4976 2.88801 0.946132 3.71334 0.56989 4.62167C0.193647 5.53 -7.32517e-09 6.50354 0 7.48671C7.32518e-09 8.46988 0.193647 9.44342 0.56989 10.3517C0.946132 11.2601 1.4976 12.0854 2.1928 12.7806L38.9122 49.5L2.1928 86.2194C1.4976 86.9146 0.946132 87.7399 0.56989 88.6483C0.193647 89.5566 0 90.5301 0 91.5133C0 92.4965 0.193647 93.47 0.56989 94.3783C0.946132 95.2867 1.4976 96.112 2.1928 96.8072C2.88801 97.5024 3.71334 98.0539 4.62167 98.4301C5.53 98.8064 6.50354 99 7.48671 99C8.46988 99 9.44342 98.8064 10.3517 98.4301C11.2601 98.0539 12.0854 97.5024 12.7806 96.8072L49.5 60.0878L86.2194 96.8072C86.9146 97.5024 87.7399 98.0539 88.6483 98.4301C89.5566 98.8064 90.5301 99 91.5133 99C92.4965 99 93.47 98.8064 94.3783 98.4301C95.2867 98.0539 96.112 97.5024 96.8072 96.8072C97.5024 96.112 98.0539 95.2867 98.4301 94.3783C98.8064 93.47 99 92.4965 99 91.5133C99 90.5301 98.8064 89.5566 98.4301 88.6483C98.0539 87.7399 97.5024 86.9146 96.8072 86.2194L60.0878 49.5L96.8072 12.7806C99.6606 9.92716 99.6606 5.12135 96.8072 2.2679Z" fill="currentColor" />
                </svg>
                <span>Fermer</span>
              </Link>
            </div>
            
            <div className="tool-group">
              {totalPages >= 1 && (
                <>
                  <button 
                    onClick={goToPreviousPage} 
                    disabled={currentPage <= 1}
                    className="tool-button nav-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 18l-6-6 6-6"/>
                    </svg>
                  </button>
                  
                  <div className="page-info">
                    <span>{currentPage} / {totalPages}</span>
                  </div>
                  
                  <button 
                    onClick={goToNextPage} 
                    disabled={currentPage >= totalPages}
                    className="tool-button nav-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </button>
                </>
              )}
              
              <button onClick={zoomOut} className="tool-button zoom-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  <line x1="8" y1="11" x2="14" y2="11"/>
                </svg>
              </button>
              
              <button onClick={resetZoom} className="tool-button zoom-btn reset-zoom">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>
              
              <button onClick={zoomIn} className="tool-button zoom-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  <line x1="11" y1="8" x2="11" y2="14"/>
                  <line x1="8" y1="11" x2="14" y2="11"/>
                </svg>
              </button>
              
              <button onClick={downloadPDF} className="tool-button download-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span>Télécharger</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="pdf-container">
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <span>Chargement du PDF...</span>
            </div>
          ) : link ? (
            <div 
              className="canvas-wrapper"
              ref={containerRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {isMobile && (
                <div className="zoom-hint">
                  <span>Utilisez deux doigts pour zoomer</span>
                </div>
              )}
              <canvas ref={canvasRef}></canvas>
            </div>
          ) : (
            <p>Le fichier PDF est introuvable.</p>
          )}
        </div>
      </main>
      <Footer visible={true} active={"lessons"} />
    </>
  );
};

export default PDFReader;